import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { Grid, Typography, Box } from "@mui/material";
import { formatDateTimestamp } from "../../utils/date";
import CustomButton from "../custom/CustomButton";
import Swal from "../../utils/swal";
import { showSuccessToast, showErrorToast } from "../../utils/swalToast";
import { useNavigate } from "react-router-dom";
// import { inverseObject } from "../../utils/format";
import { formatCamelCaseToCapital, inverseObject } from "../../utils/format";

function ConfirmationUserRequestData() {
  const navigate = useNavigate();
  const [changes, setChanges] = useState({});
  const [oldData, setOldData] = useState({});
  const { requestId } = useParams();
  const { userId } = useParams();
  const keysToExclude = [
    "id",
    "uuid",
    "privilege",
    "year_join",
    "card_num",
    "is_member",
    "role",
    "id_users",
    "email",
    "is_login",
    "last_login",
    "expired_card_at",
    "member_since",
    "member_ceased",
    "tanggal_meninggal",
    "foto",
    "is_deleted",
  ];

  const keyMapping = {
    ktp: "ktp",
    nama_lengkap: "namaLengkap",
    gelar_depan: "gelarDepan",
    gelar_belakang: "gelarBelakang",
    tempat_lahir: "tempatLahir",
    tanggal_lahir: "tanggalLahir",
    negara_asal: "negaraAsal",
    provinsi_institusi: "provinsiInstitusi",
    kota_institusi: "kotaInstitusi",
    institusi_asal: "namaInstitusi",
    prodi: "programStudi",
    jenjang: "jenjangMengajar",
    pendidikan: "pendidikanTerakhir",
    jenjang_akademik: "jenjangAkademik",
    keahlian_1: "bidangKeahlian1",
    keahlian_2: "bidangKeahlian2",
    keahlian_3: "bidangKeahlian3",
    no_hp: "nomorHandphone",
    google_scholar_id: "googleScholarId",
  };

  const handleAcceptConfirmation = () => {
    Swal.fire({
      title: "Konfirmasi",
      text: "Apakah Anda yakin ingin menerima perubahan ini?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
      confirmButtonText: "Terima",
      cancelButtonText: "Batal",
    }).then((result) => {
      if (result.isConfirmed) {
        acceptRequest();
      }
    });
  };

  const handleRejectConfirmation = () => {
    Swal.fire({
      title: "Konfirmasi",
      text: "Apakah Anda yakin ingin menolak perubahan ini?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
      confirmButtonText: "Tolak",
      cancelButtonText: "Batal",
    }).then((result) => {
      if (result.isConfirmed) {
        rejectRequest();
      }
    });
  };

  const acceptRequest = async () => {
    try {
      const userUpdateResponse = await apiClient.put("/users", {
        ...changes,
        userId,
      });
      if (userUpdateResponse.status === 200) {
        const userRequestUpdateResponse = await apiClient.put(
          "/users/request",
          { requestId }
        );

        if (userRequestUpdateResponse.status === 201) {
          showSuccessToast("User request update successful");
          navigate("/edit-user");
        } else {
          showErrorToast("Error updating user request data");
        }
      } else {
        showErrorToast("Error updating user data");
      }
    } catch (error) {
      showErrorToast("Error updating data");
    }
  };

  const rejectRequest = async () => {
    try {
      const userUpdateResponse = await apiClient.put("/users", {
        ...changes,
        userId,
      });
      if (userUpdateResponse.status === 200) {
        const userRequestUpdateResponse = await apiClient.put(
          "/users/request-reject",
          { requestId }
        );
        if (userRequestUpdateResponse.status === 201) {
          showSuccessToast(userRequestUpdateResponse.data.msg);
          navigate("/edit-user");
        }
      }
    } catch (error) {
      showErrorToast(error.userRequestUpdateResponse.data.msg);
    }
  };

  const getFullCountryName = async (code) => {
    try {
      const response = await apiClient.get("/countries", {
        params: { code },
      });

      if (response.data.data.length > 0) {
        return response.data.data[0].name;
      } else {
        console.warn("Negara tidak ditemukan");
        return null;
      }
    } catch (error) {
      console.error("Error fetching country name", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchChanges = async () => {
      try {
        const response = await apiClient.get(
          `/users/request/changes?request_id=${requestId}`
        );

        let responseData = response.data.data;

        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            console.error("Error parsing response data:", parseError);
            return;
          }
        }

        if (responseData) {
          responseData.tanggalLahir = formatDateTimestamp(
            responseData.tanggalLahir
          );

          // Cek apakah negaraAsal ada, lalu ubah menjadi nama lengkap
          if (responseData.negaraAsal) {
            const fullCountryName = await getFullCountryName(
              responseData.negaraAsal
            );
            responseData.negaraAsal =
              fullCountryName || responseData.negaraAsal; // Gunakan kode asli jika tidak ditemukan
          }

          setChanges(responseData);
        } else {
          console.error("Response data is null or undefined");
        }
      } catch (error) {
        console.error("Error fetching request changes", error);
      }
    };

    if (requestId) {
      fetchChanges();
    }
  }, [requestId]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await apiClient.get(`/users?uuid=${userId}`);

        let userData = response.data.data[0];
        userData.tanggal_lahir = formatDateTimestamp(userData.tanggal_lahir);

        // Konversi negara asal menjadi nama lengkap
        if (userData.negara_asal) {
          const fullCountryName = await getFullCountryName(
            userData.negara_asal
          );
          userData.negara_asal = fullCountryName || userData.negara_asal;
        }

        setOldData(userData);
      } catch (error) {
        console.error("Error fetching request changes", error);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  return (
    <Grid container spacing={2}>
      <Grid item md={6} xs={12}>
        <Typography variant="h6">Data Lama</Typography>
        {Object.entries(oldData)
          .filter(([key]) => !keysToExclude.includes(key))
          .map(([key, value]) => (
            <Typography key={key} component="div">
              <Box component="span" fontWeight="fontWeightBold">
                {formatCamelCaseToCapital(keyMapping[key])} :
              </Box>
              {value}
            </Typography>
          ))}
      </Grid>
      <Grid item md={6} xs={12}>
        <Typography variant="h6">Data Baru</Typography>
        {Object.entries(changes).map(([key, value]) => (
          <Typography key={key} component="div">
            <Box component="span" fontWeight="fontWeightBold">
              {formatCamelCaseToCapital(key)} :
            </Box>
            <span
              style={{
                color:
                  oldData[inverseObject(keyMapping)[key]] !== changes[key]
                    ? "red"
                    : "inherit",
              }}
            >
              {value}
            </span>
          </Typography>
        ))}
      </Grid>
      <Grid item md={6} xs={12}>
        <CustomButton
          text="Terima Perubahan"
          baseColor="#003158"
          hoverColor="#00213A"
          onClick={handleAcceptConfirmation}
        />
      </Grid>
      <Grid item md={6} xs={12}>
        <CustomButton
          text="Tolak Perubahan"
          baseColor="#A92027"
          hoverColor="#8C1016"
          onClick={handleRejectConfirmation}
        />
      </Grid>
    </Grid>
  );
}

export default ConfirmationUserRequestData;
